import { Suspense, useEffect, useState } from 'react';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/header/Header';
import { DoubleRightOutlined, DoubleLeftOutlined, BookOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import './style.css';
import { RootState, useAppDispatch } from '../../app/store';
import { ROLES } from '../../utils/constant';
import { fetchMinistriesMenu, fetchIndicatorsMenu, fetchReportsMenu } from './layoutsApi';
import { useParams } from 'react-router-dom';

const { Header, Content, Sider } = Layout;
type MenuItem = Required<MenuProps>['items'][number];
interface ItemType {
  key: string; // Add other properties as necessary
  label: string;
  children?: ItemType[];
}

const SetupLayout = () => {
  const { t } = useTranslation();
  const { indicatorId } = useParams<{ indicatorId: string }>();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { isLoading } = useSelector((state: RootState) => state.loading);
  const loginUser = useSelector((state: RootState) => state.auth.loginUser);
  const { indicators } = useSelector((state: RootState) => state.layoutData);
  // const [items, setItems] = useState<MenuItem[]>([]);
  const [newMenuItems, setNewMenuItems] = useState<ItemType[]>([]);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [indicatorMenu, setIsIndicatorMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFetchedIndicatorsMenu, setHasFetchedIndicatorsMenu] = useState(false);
  const [hasFetchedReportsMenu, setHasFetchedReportsMenu] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>(
    showNewMenu && newMenuItems.length > 0 ? newMenuItems[0].key : 'home'
  );

  useEffect(() => {
    if (showNewMenu && newMenuItems.length > 0) {
      setSelectedKey(newMenuItems[0].key);

    }
  }, [showNewMenu]);

  useEffect(() => {
    const selectedKey = localStorage.getItem('selectedKey');
    if (location.pathname.startsWith('/indicator')) {
      if (!hasFetchedIndicatorsMenu && selectedKey) {
        setLoading(true);
        dispatch(fetchIndicatorsMenu(parseInt(selectedKey)))
          .unwrap()
          .then((response) => {
            setLoading(false);
            if (response?.status === 'success') {
              localStorage.setItem('ministryName', response.data.ministryName);

              localStorage.setItem('indicatorMenu', JSON.stringify(response.data.indicatormenu));

              const newMenuData = response.data.indicatormenu.map((item: any) => ({
                key: item.id.toString(),
                label: item.name,
              }));

              setNewMenuItems(newMenuData);
              setShowNewMenu(true);
              setIsIndicatorMenu(true);

              // Set flag to avoid future redundant calls
              localStorage.setItem('fetchedIndicators', 'true');
              setSelectedKey(indicatorId ? indicatorId : '');
            }
          })
          .catch((error) => {
            setLoading(false);
            console.error('Error fetching indicators menu:', error);
          });
      }
    }
    else if (location.pathname.startsWith('/reports')) {

      if (!hasFetchedReportsMenu && selectedKey) {
        setLoading(true);
        dispatch(fetchReportsMenu(parseInt(selectedKey)))
          .unwrap()
          .then((response) => {
            setLoading(false);
            if (response?.status === 'success') {
              localStorage.setItem('ministryName', response.data.ministryName);

              localStorage.setItem('reportMenu', JSON.stringify(response.data.reportmenu));

              const newMenuData = response.data.reportmenu.map((item: any) => ({
                key: item.id.toString(),
                label: item.name,
              }));

              setNewMenuItems(newMenuData);
              setShowNewMenu(true);
              // Set flag to avoid future redundant calls
              localStorage.setItem('fetchedReports', 'true');
              setSelectedKey(indicatorId ? indicatorId : '');
            }
          })
          .catch((error) => {
            setLoading(false);
            console.error('Error fetching reports menu:', error);
          });
      }
    }
    else {
      // Default case if neither indicator nor report is selected
      transformIndicatorsToMenu(indicators);
      setShowNewMenu(false);
      setIsIndicatorMenu(false);
      setLoading(true);
      dispatch(fetchMinistriesMenu());
      if (location.pathname.startsWith('/home')) {
        setSelectedKey('home');
      }

    }
  }, [location.pathname]);

  // Menu items for side navigation
  const items: MenuItem[] = [];

  const transformIndicatorsToMenu = (indicators: any): MenuItem[] => {
    return indicators.map((indicator: any) => {
      const transformed: MenuItem = {
        key: indicator.id.toString(),
        label: (
          <span
            onClick={(e) => {
              if (indicator.click) {
                handleParentMenuClick(indicator.id.toString(), indicator.name, e);
              } else {
                e.stopPropagation(); // Prevents click if indicator.click is false
              }
            }}
            style={{ cursor: indicator.click ? 'pointer' : 'not-allowed' }}
          >
            {indicator.name}
          </span>
        ),
        disabled: false, // Allow submenus to still expand even if main item click is disabled
        children: indicator.children ? transformIndicatorsToMenu(indicator.children) : undefined,
      };
      return transformed;
    });
  };


  const transformReportsToMenu = (indicators: any): MenuItem[] => {
    return indicators.map((indicator: any) => {
      const transformed: MenuItem = {
        key: indicator.id.toString(),
        label: (
          <span
            onClick={(e) => {
              if (indicator.click) {
                handleReportsParentMenuClick(indicator.id.toString(), indicator.name, e);
              } else {
                e.stopPropagation(); // Prevents click if indicator.click is false
              }
            }}
            style={{ cursor: indicator.click ? 'pointer' : 'not-allowed' }}
          >
            {indicator.name}
          </span>
        ),
        disabled: false, // Allow submenus to still expand even if main item click is disabled
        children: indicator.children ? transformReportsToMenu(indicator.children) : undefined,
      };
      return transformed;
    });
  };

  const indicatorsMenu = transformIndicatorsToMenu(indicators);
  const reportsMenu = transformReportsToMenu(indicators);


  const setupMenu = {
    key: 'setup',
    label: t('menu.setup'),
    icon: <span style={{ width: '18px' }} className='menuUser' ></span>,
    children: [
      { key: 'user-management', label: t('menu.setup.user-management') },
      { key: 'indicators-mapping', label: t('mapping-indicator.ministries-departments') },
      { key: 'reports-mapping', label: t('menu.setup.report') }
    ]
  };

  const dataCollectionMenu = {
    key: 'data-collection',
    label: t('menu.data-collection'),
    icon: <span style={{ width: '20px' }} className='menuDataCollection' />,
    children: indicatorsMenu
  };

  const reportMenu = {
    key: 'reports',
    label: t('reports-mapping.reports'),
    icon: <span style={{ width: '18px' }} className='menuReport' ></span>,
    children: reportsMenu
  };

  // As per login user role and permission add side navigation 
  if (loginUser?.user?.role_id === ROLES.SUPER_ADMIN || loginUser?.user?.role_id === ROLES.DATA_MANAGER) {
    items.push(setupMenu);
  }
  items.push(dataCollectionMenu);
  items.push(reportMenu);

  // Fetch current selected menu key
  const getSelectedKeys = (): string[] => {
    if (location.pathname === '/setup') return ['setup'];
    if (location.pathname.includes('/setup/user-management')) return ['user-management'];
    if (location.pathname.includes('/setup/indicators-mapping')) return ['indicators-mapping'];
    if (location.pathname.includes('/setup/reports-mapping')) return ['reports-mapping'];
    return ['setup'];
  };

  const handleParentMenuClick = (selectedKey: string, keyName: string, e: React.MouseEvent<HTMLElement>) => {
    // console.log('keyName', keyName);
    e.stopPropagation(); // Prevent click on the label from bubbling to the collapse icon
    localStorage.setItem('selectedKey', selectedKey);

    setHasFetchedIndicatorsMenu(true); // Set flag to true to prevent duplicate calls
    dispatch(fetchIndicatorsMenu(parseInt(selectedKey)))
      .unwrap()
      .then((response) => {
        if (response?.status === 'success') {
          localStorage.setItem('ministryName', response.data.ministryName);
          localStorage.setItem('indicatorMenu', JSON.stringify(response.data.indicatormenu));

          const newMenuData = response.data.indicatormenu.map((item: any) => ({
            key: item.id.toString(),
            label: item.name,
          }));

          setNewMenuItems(newMenuData);
          setShowNewMenu(true);
          setOpenKeys([]); // Collapse the main menu after fetching

          // Navigate to the first item's route
          if (newMenuData.length > 0) {
            setIsIndicatorMenu(true);
            navigate(`/indicator/${newMenuData[0].key}`); // This redirects to the first item's route
            setSelectedKey(newMenuItems[0].key);
          }
          else {
            navigate(`/indicator/datanotfound`);

          }
        }
      })
      .catch((error) => console.error("Failed to fetch indicators menu:", error));
  };

  const handleReportsParentMenuClick = (selectedKey: string, keyName: string, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Prevent click on the label from bubbling to the collapse icon

    localStorage.setItem('selectedKey', selectedKey)
    setHasFetchedReportsMenu(true); // Set flag to true to prevent duplicate calls
    dispatch(fetchReportsMenu(parseInt(selectedKey)))
      .unwrap()
      .then((response) => {
        if (response?.status === 'success') {
          localStorage.setItem('ministryName', response.data.ministryName);
          localStorage.setItem('reportMenu', JSON.stringify(response.data.reportmenu));

          const newMenuData = response.data.reportmenu.map((item: any) => ({
            key: item.id.toString(),
            label: item.name,
          }));

          setNewMenuItems(newMenuData);
          setShowNewMenu(true);
          setOpenKeys([]); // Collapse the main menu after fetching

          // Navigate to the first item's route
          if (newMenuData.length > 0) {
            navigate(`/reports/${newMenuData[0].key}`); // This redirects to the first item's route
            setSelectedKey(newMenuItems[0].key);

          }
          else {
            navigate(`/reports/datanotfound`);

          }
        }
      })
      .catch((error) => console.error("Failed to fetch reports menu:", error));
  };


  const handleMenuClick = (e: any) => {
    setSelectedKey(e.key);
    // Check if we're in the new menu state
    if (showNewMenu) {
      // Handle new menu navigation
      handleOnClickTemplates(e);
    } else {
      // Handle navigation for static menu items
      handleMenuNavigation(e);
    }
  };

  const indicatorTemplateMenu: MenuItem[] = [];

  const templateMenu =
  {
    key: 'list-templates',
    icon: <BookOutlined className='bookOutlinedIcon' />,
    label: location.pathname.startsWith('/indicator') ? t('list.of.indicator.templates') : t('list.of.reports.templates'),
    children: newMenuItems,
  }
  indicatorTemplateMenu.push(templateMenu);

  const handleOnClickTemplates = (e: any) => {
    const selectedKey = e.key;
    setSelectedKey(selectedKey);
    if (selectedKey === "home" || selectedKey === "t-home") {
      setShowNewMenu(false);
      setIsIndicatorMenu(false);
      setNewMenuItems([]);
      setOpenKeys([]);
      navigate(`/home`);
      // window.location.reload();
    }
    else if (indicatorMenu) {
      // Find the selected item from newMenuItems
      const selectedMenuItem = newMenuItems.find((item) => item.key === selectedKey);

      if (selectedMenuItem) {
        // Navigate to /indicator/{key} on item click
        navigate(`/indicator/${parseInt(selectedMenuItem.key)}`);
      }
    }
    else {
      // Find the selected item from newMenuItems
      const selectedMenuItem = newMenuItems.find((item) => item.key === selectedKey);

      if (selectedMenuItem) {
        // Navigate to /indicator/{key} on item click
        navigate(`/reports/${parseInt(selectedMenuItem.key)}`);
      }
    }
  };


  const handleMenuNavigation = (e: any) => {
    const key = e.key;
    // Handle navigation based on the key
    switch (key) {
      case 'user-management':
        navigate('/setup/user-management');
        break;
      case 'indicators-mapping':
        navigate('/setup/indicators-mapping');
        break;
      case 'reports-mapping':
        navigate('/setup/reports-mapping');
        break;
      case 'setup':
        navigate('/setup/user-management');
        break;
      default:
        // This will handle cases where key is not one of the static items
        if (location.pathname.startsWith('/indicator')) {
          navigate(`/indicator/${key}`);
        }
        break;
    }
  };
  // Toggle open keys to control submenu behavior
  const onMenuClick = (item: { keyPath: any[]; }) => {
    const key = item.keyPath[1]; // Get the parent submenu key
    setOpenKeys((prevOpenKeys) =>
      prevOpenKeys.includes(key) ? prevOpenKeys.filter((k) => k !== key) : [...prevOpenKeys, key]
    );
    handleMenuClick(item);
  };


  const [divHeight, setDivHeight] = useState(window.innerHeight - 60);

  useEffect(() => {
    const handleResize = () => {
      setDivHeight(window.innerHeight - 60);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Suspense fallback={isLoading}> <Layout style={{ minHeight: '100vh' }}>

      <Header className="site-layout-background">
        <AppHeader />
      </Header>
      <Layout style={{ height: 'auto', overflowX: 'hidden', overflowY: 'auto' }}>
        <Sider
          className="leftMenu"
          // collapsible
          // collapsed={collapsed}
          onCollapse={(collapsed) => setCollapsed(collapsed)} // Collapse handler without API call
          width="80px"
          trigger={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
          onMouseEnter={() => setIsHovered(true)}  // Set hover state to true on mouse enter
          onMouseLeave={() => setIsHovered(false)} // Set hover state to false on mouse leave
          style={{ height: divHeight }}
        >


          <Menu
            mode="inline"
            selectedKeys={[selectedKey]} // Dynamically track the selected key
            openKeys={openKeys}
            //onOpenChange={setOpenKeys} 
            onOpenChange={(keys) => setOpenKeys(keys)} // Keep open keys consistent
            style={{ height: '100%', borderRight: 0 }}
            items={showNewMenu ? indicatorTemplateMenu : items}
            onClick={onMenuClick}
            triggerSubMenuAction="click"  // Only open submenus on click
          />
          <div className='over_div'></div>
        </Sider>
        <Content style={{ padding: '0 0', minHeight: 280 }} className={`main-content ${isHovered ? 'hovered' : ''}`}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
    </Suspense>


  );

};

export default SetupLayout;